import getRecaptchaToken from '@/utils/getRecaptchaToken'
import { getTrustTokenFor, removeTrustTokenFor } from '@/utils/trustToken'
import { isTwoFactorChallenge } from '@/utils/twoFactor'
import Api from '@/axios/api'

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred, please try again later or contact our support'

function getErrorMessage(error) {
  return Array.isArray(error?.response?.data) ? error?.response?.data?.at(0) : error?.response?.data?.message || error?.response?.data || DEFAULT_ERROR_MESSAGE
}

async function signInAction ({ email, password }) {
  if (!email || !password) { return Promise.reject() }

  try {
    const recaptchaToken = await getRecaptchaToken()
    const trustToken = getTrustTokenFor(email)
    const headers = {}

    if (trustToken) {
      headers['X-Tfa-Trust-Token'] = trustToken
    }

    return Api.post('/customer/login', {
      email,
      password,
      recaptcha_token: recaptchaToken,
    }, { headers })
      .then((data) => {
        // A challenge coming back despite a stored trust token means the token no
        // longer buys anything: it either expired, or the backend revoked all
        // trusted devices (any 2FA settings change does), or the account switched
        // to the SMS channel, where trust tokens are ignored outright.
        if (trustToken && isTwoFactorChallenge(data)) {
          removeTrustTokenFor(email)
        }

        return data
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)

        return Promise.reject(errorMessage)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

async function verify2FAAction ({ challenge_token, code }) {
  if (!challenge_token || !code) { return Promise.reject() }

  return Api.post('/customer/2fa/verify', {
    challenge_token,
    code,
  })
    .catch((error) => {
      return Promise.reject(error?.response?.data ?? { message: getErrorMessage(error) })
    })
}

async function resend2FAAction ({ challenge_token }) {
  if (!challenge_token) { return Promise.reject() }

  return Api.post('/customer/2fa/resend', {
    challenge_token,
  })
    .catch((error) => {
      return Promise.reject(error?.response?.data ?? { message: getErrorMessage(error) })
    })
}

async function sms2FAStatusAction ({ challenge_token }) {
  if (!challenge_token) { return Promise.reject() }

  return Api.post('/customer/2fa/sms-status', {
    challenge_token,
  })
    .catch((error) => {
      return Promise.reject({
        status: error?.response?.status,
        ...(error?.response?.data ?? { message: getErrorMessage(error) }),
      })
    })
}

async function email2FAFallbackAction ({ challenge_token }) {
  if (!challenge_token) { return Promise.reject() }

  return Api.post('/customer/2fa/email-fallback', {
    challenge_token,
  })
    .catch((error) => {
      return Promise.reject(error?.response?.data ?? { message: getErrorMessage(error) })
    })
}

async function signUpAction ({ firstName, lastName, email, password, company }) {
  if (!email || !password || !firstName || !lastName) { return Promise.reject() }

  try {
    const recaptchaToken = await getRecaptchaToken()

    return Api.post('/account/create', {
      firstname: firstName,
      lastname: lastName,
      email: email,
      password: password,
      company: company,
      recaptcha_token: recaptchaToken,
    })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)

        return Promise.reject(errorMessage)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

async function signInWithSSOAction ({ email }) {
  if (!email) { return Promise.reject() }

  try {
    const recaptchaToken = await getRecaptchaToken()

    return Api.post('/customer/login-sso', {
      email,
      recaptcha_token: recaptchaToken,
    })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        return Promise.reject(errorMessage)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

async function forgotPasswordAction ({ email }) {
  if (!email) { return Promise.reject() }

  try {
    const recaptchaToken = await getRecaptchaToken()

    return Api.post('/account/forgot-password', {
      email,
      recaptcha_token: recaptchaToken,
    })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)

        return Promise.reject(errorMessage)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

async function reSendActivationEmail (email) {
  if (!email) { return Promise.reject() }

  try {
    const recaptchaToken = await getRecaptchaToken()

    return Api.post('/email/verify/resend', {
      email,
      recaptcha_token: recaptchaToken,
    })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)

        return Promise.reject(errorMessage)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

async function checkIfAccountExist (email) {
  if (!email) { return Promise.reject() }
  try {
    return Api.get('/checkout/account-exists', {
      params: {
        email
      }
    })
      .catch((e) => {
        return Promise.reject(e)
      })
  } catch (error) {
    return Promise.reject(error)
  }
}

export {
  signInAction,
  verify2FAAction,
  resend2FAAction,
  sms2FAStatusAction,
  email2FAFallbackAction,
  signUpAction,
  signInWithSSOAction,
  forgotPasswordAction,
  reSendActivationEmail,
  checkIfAccountExist
}
